/* Kaching Bundle - Dual ATC - Prestige v7+ */
/*
  2026-10-06 fix: Kaching blocks clicks on the main Add to Cart at WINDOW level
  (capture phase) whenever the bundle is selected but its sizes aren't picked.
  That ran before this script's own handler, so the main ATC silently did nothing.
  The main-ATC handler is now driven from a window-level capture listener that is
  registered as soon as this file loads (loaded WITHOUT defer in buy-buttons.liquid),
  so it always runs before Kaching's listener.
  Backup of the previous version: assets/kaching-bundles-atc-backup-2026-10-06.js

  2026-10-06 follow-up: with no size picked, the main ATC now shows the theme's red
  size prompt without touching the bundle, and after a single-item add the bundle box
  is re-opened so its size dropdowns stay visible.

  2026-10-06 follow-up 2: a size pre-selected by the URL (?variant=…, e.g. ad and
  Google Shopping links) now counts as chosen, so Add to Cart works without re-picking.
*/
(() => {
  // Guard against the file running twice (e.g. snippet rendered more than once).
  if (window.__dualAtcLoaded) return;
  window.__dualAtcLoaded = true;

  const DEBUG = true;
  const PRODUCT_ROOT_SELECTOR = '.product-info__block-list';
  const FORM_SELECTOR = 'form.shopify-product-form[action*="/cart/add"]';
  const NATIVE_ATC_SELECTOR = 'buy-buttons button[type="submit"]';
  const BUNDLE_ATC_SELECTOR = '[data-kaching-bundle-atc]';
  const KACHING_SESSION_SELECTOR = '[name="properties[__kaching_session_id]"]';
  const initializedRoots = new WeakSet();

  // Size pre-selected by the page URL (e.g. ad / Google Shopping links ending ?variant=123).
  // Read as soon as this file loads, before Kaching can change the URL.
  const initialUrlVariantId = new URL(window.location.href).searchParams.get('variant');

  // Main-ATC click handlers, one per product form. Called from a window-level
  // capture listener so they run BEFORE Kaching's own window-level listener.
  const nativeAtcClickHandlers = new Set();

  window.addEventListener('click', event => {
    nativeAtcClickHandlers.forEach(handler => handler(event));
  }, true);

  function log(label, data = null) {
    if (!DEBUG) return;
    data !== null ? console.log(`[Dual ATC] ${label}`, data) : console.log(`[Dual ATC] ${label}`);
  }

  function initDualAtc(productRoot) {
    if (initializedRoots.has(productRoot)) return;

    function getForm() {
      return productRoot.querySelector(FORM_SELECTOR);
    }

    function getNativeAtc() {
      return getForm()?.querySelector(NATIVE_ATC_SELECTOR) || null;
    }

    function getBundleAtc() {
      return getForm()?.querySelector(BUNDLE_ATC_SELECTOR) || null;
    }

    function getVariantPicker() {
      const form = getForm();
      if (!form) return null;

      return productRoot.querySelector(`variant-picker[form-id="${CSS.escape(form.id)}"]`) ||
        productRoot.querySelector('variant-picker');
    }

    function getKachingElements() {
      const form = getForm();
      if (!form) return {};

      const root = form.querySelector('kaching-bundle');
      if (!root) return {};

      const block = root.querySelector('kaching-bundles-block');
      const bar = root.querySelector('.kaching-bundles__bar[data-deal-bar-id]');

      return {
        root,
        block,
        bar,
        dealId: bar?.dataset.dealBarId || null,
        radio: bar?.querySelector('input[type="radio"]') || null,
        barMain: bar?.querySelector('.kaching-bundles__bar-main') || null
      };
    }

    const form = getForm();
    const bundleAtc = getBundleAtc();
    const initial = getKachingElements();

    if (!form || !getNativeAtc() || !bundleAtc || !getVariantPicker() || !initial.root || !initial.block || !initial.bar || !initial.dealId || !initial.radio || !initial.barMain) return;

    initializedRoots.add(productRoot);

    const dealId = initial.dealId;

    let currentDealId = initial.radio.checked || initial.bar.classList.contains('kaching-bundles__bar--selected') ? dealId : null;
    let regularVariantId = null;
    let regularOptions = [];
    let latestVariantState = null;
    let regularSelectionPending = false;
    let pendingRegularOptions = null;
    let bypassRegularGuard = false;
    let regularModePromise = null;
    let bundleSubmitInFlight = false;

    /* KACHING EVENTS */

    initial.block.addEventListener('deal-bar-selected', event => {
      currentDealId = event.detail?.dealBarId ?? null;

      log('deal-bar-selected', {
        dealBarId: currentDealId,
        preselected: event.detail?.preselected
      });
    });

    initial.block.addEventListener('variants-changed', event => {
      latestVariantState = event.detail;

      log('variants-changed', {
        variantIdQuantities: event.detail?.variantIdQuantities,
        bundleProducts: event.detail?.bundleProducts,
        validation: event.detail?.validation
      });
    });

    /* PRESTIGE REGULAR VARIANT */

    function getNativeOptionInputs() {
      const form = getForm();
      if (!form?.id) return [];

      return Array.from(
        document.querySelectorAll(`input[data-option-position][form="${CSS.escape(form.id)}"]`)
      );
    }

    function getCheckedNativeOptions() {
      return getNativeOptionInputs()
        .filter(input => input.checked)
        .map(input => ({
          position: input.dataset.optionPosition,
          value: input.value
        }));
    }

    function getPendingOptionsForInput(input) {
      const selections = new Map();

      getCheckedNativeOptions().forEach(option => {
        selections.set(option.position, option.value);
      });

      selections.set(input.dataset.optionPosition, input.value);

      return Array.from(selections, ([position, value]) => ({
        position,
        value
      }));
    }

    document.addEventListener('change', event => {
      if (!event.isTrusted) return;
      if (!(event.target instanceof HTMLInputElement)) return;

      const input = event.target;

      if (!input.matches('input[data-option-position]')) return;
      if (input.form !== getForm()) return;

      regularSelectionPending = true;
      pendingRegularOptions = getCheckedNativeOptions();

      log('Real Prestige option change', {
        position: input.dataset.optionPosition,
        value: input.value,
        pendingRegularOptions
      });
    }, true);

    productRoot.addEventListener('variant:change', event => {
      const variantId = event.detail?.variant?.id ?? null;

      if (!regularSelectionPending) {
        log('Ignoring Kaching-driven Prestige variant change', { variantId });
        return;
      }

      regularSelectionPending = false;
      regularVariantId = variantId ? String(variantId) : null;
      regularOptions = pendingRegularOptions || getCheckedNativeOptions();
      pendingRegularOptions = null;

      log('Saved REGULAR Prestige variant', {
        regularVariantId,
        regularOptions
      });
    });

    /* URL PRE-SELECTED VARIANT */
    /* If the page loaded with ?variant=… and the theme shows that size as chosen,
       treat it as the shopper's selection. Only trusted if the variant belongs to this product. */

    function getPreselectedVariantId() {
      if (!initialUrlVariantId) return null;

      const picker = getVariantPicker();
      if (!picker || picker.hasAttribute('data-unselected')) return null;

      const product = window.ShopifyAnalytics?.meta?.product;
      const productId = getForm()?.querySelector('input[name="product-id"]')?.value;

      if (!product || String(product.id) !== String(productId)) return null;

      const belongsToProduct = (product.variants || []).some(variant => String(variant.id) === initialUrlVariantId);

      return belongsToProduct ? initialUrlVariantId : null;
    }

    /* RESTORE REGULAR VARIANT */

    function restoreRegularVariant() {
      const form = getForm();
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const inputs = getNativeOptionInputs();

      if (!regularVariantId) {
        inputs.forEach(input => {
          input.checked = false;
        });

        log('Restored regular state: unselected.');
        return;
      }

      inputs.forEach(input => {
        input.checked = regularOptions.some(option =>
          option.position === input.dataset.optionPosition &&
          option.value === input.value
        );
      });

      idInput.value = regularVariantId;

      log('Restored REGULAR variant', {
        regularVariantId,
        formVariantId: idInput.value
      });
    }

    /* KACHING SESSION */

    function getKachingSessionInput() {
      return getForm()?.querySelector(KACHING_SESSION_SELECTOR) || null;
    }

    function enableKachingSession() {
      const input = getKachingSessionInput();

      if (!input) {
        log('No Kaching session input found.');
        return;
      }

      input.disabled = false;

      log('Kaching session ENABLED', {
        value: input.value
      });
    }

    function disableKachingSession() {
      const input = getKachingSessionInput();

      if (!input) {
        log('No Kaching session input found.');
        return;
      }

      input.disabled = true;

      log('Kaching session DISABLED', {
        value: input.value
      });
    }

    /* HELPERS */

    function nextFrame() {
      return new Promise(resolve => requestAnimationFrame(resolve));
    }

    function waitForKachingTransition(expectedDealId, timeout = 2500) {
      const { block } = getKachingElements();

      if (!block) {
        return Promise.reject(new Error('Kaching bundles block not found.'));
      }

      return new Promise((resolve, reject) => {
        let dealChanged = false;
        let variantsChanged = false;

        const timer = setTimeout(() => {
          cleanup();
          reject(new Error(`Timed out waiting for Kaching transition to ${expectedDealId}`));
        }, timeout);

        function cleanup() {
          clearTimeout(timer);
          block.removeEventListener('deal-bar-selected', handleDealChange);
          block.removeEventListener('variants-changed', handleVariantsChange);
        }

        function maybeResolve() {
          if (!dealChanged || !variantsChanged) return;
          cleanup();
          resolve();
        }

        function handleDealChange(event) {
          const selectedDealId = event.detail?.dealBarId ?? null;
          if (selectedDealId !== expectedDealId) return;

          dealChanged = true;
          log('Expected deal state reached', selectedDealId);
          maybeResolve();
        }

        function handleVariantsChange(event) {
          latestVariantState = event.detail;
          variantsChanged = true;

          log('Variant state updated during transition', event.detail);
          maybeResolve();
        }

        block.addEventListener('deal-bar-selected', handleDealChange);
        block.addEventListener('variants-changed', handleVariantsChange);
      });
    }

    /* KACHING OFF */

    async function ensureBundleInactive() {
      const { barMain } = getKachingElements();

      if (!barMain) {
        throw new Error('Kaching bundle control not found.');
      }

      if (currentDealId === null) {
        log('Bundle already OFF.');
        return;
      }

      log('Turning bundle OFF.');

      const transition = waitForKachingTransition(null);
      barMain.click();

      await transition;
      await nextFrame();

      log('Bundle OFF confirmed.');
    }

    /* KACHING ON */

    async function ensureBundleActive() {
      const { barMain } = getKachingElements();

      if (!barMain) {
        throw new Error('Kaching bundle control not found.');
      }

      if (currentDealId === dealId) {
        log('Bundle already ON.');
        return;
      }

      log('Turning bundle ON.', { dealId });

      enableKachingSession();

      const transition = waitForKachingTransition(dealId);
      barMain.click();

      await transition;
      await nextFrame();

      log('Bundle ON confirmed.');
    }

    /* KEEP BUNDLE BOX OPEN */
    /* Re-selects the bundle in Kaching (same as on page load) so its size dropdowns
       stay visible. The Kaching session stays disabled, so single-item adds are unaffected. */

    function restoreBundleDisplay() {
      if (currentDealId !== null) return;

      const { bar, barMain } = getKachingElements();
      if (!bar || !barMain) return;

      bar.classList.add('kaching-bundles__bar--selected');
      barMain.click();

      log('Bundle box re-opened.');
    }

    function restoreBundleDisplayAfterCartAdd() {
      let done = false;
      let timer = null;

      function finish() {
        if (done) return;
        done = true;

        clearTimeout(timer);
        document.removeEventListener('variant:add', finish, true);
        document.removeEventListener('cart:error', finish, true);

        setTimeout(restoreBundleDisplay, 300);
      }

      timer = setTimeout(finish, 5000);
      document.addEventListener('variant:add', finish, true);
      document.addEventListener('cart:error', finish, true);
    }

    /* CURRENT NATIVE ATC */

    function isCurrentNativeAtcEvent(event) {
      if (!(event.target instanceof Element)) return false;

      const currentNativeAtc = getNativeAtc();
      if (!currentNativeAtc) return false;

      return event.target.closest(NATIVE_ATC_SELECTOR) === currentNativeAtc;
    }

    /* REGULAR ATC */
    /* Runs from the window-level capture listener at the top of this file,
       so it gets the click before Kaching can block it. */

    nativeAtcClickHandlers.add(async event => {
      if (!isCurrentNativeAtcEvent(event)) return;

      if (bypassRegularGuard) {
        log('Native ATC bypass.');
        return;
      }

      if (!event.isTrusted) {
        log('Ignoring programmatic native ATC click.');
        return;
      }

      // No size picked yet: leave the bundle alone (so its size dropdowns stay visible)
      // and show the theme's own "please select a size" prompt (red outline + dropdown).
      const picker = getVariantPicker();

      if (picker?.hasAttribute('data-unselected')) {
        event.preventDefault();
        event.stopImmediatePropagation();

        picker.classList.add('size-error');

        const selection = picker.querySelector('variant-selection');

        if (typeof selection?.reportMissingSelection === 'function') {
          selection.reportMissingSelection();
        }

        log('Regular ATC blocked: no size selected. Showing size prompt.');
        return;
      }

      log('REGULAR ATC clicked', {
        regularVariantId,
        currentDealId,
        switchingToRegular: !!regularModePromise
      });

      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        if (currentDealId !== null) {
          if (!regularModePromise) {
            regularModePromise = ensureBundleInactive();
          }

          await regularModePromise;
        }

        if (currentDealId !== null) {
          log('Bundle still ON after pending regular transition. Forcing OFF again.', {
            currentDealId
          });

          regularModePromise = ensureBundleInactive();
          await regularModePromise;
        }

        if (currentDealId !== null) {
          throw new Error(`Refusing regular ATC because Kaching is still active: ${currentDealId}`);
        }

        disableKachingSession();
        restoreRegularVariant();
        await nextFrame();

        const currentForm = getForm();
        const currentNativeAtc = getNativeAtc();

        if (!currentForm || !currentNativeAtc) {
          throw new Error('Current Prestige form/ATC not found.');
        }

        delete currentForm.dataset.kachingBundleSubmit;

        if (!regularVariantId) {
          const firstOption = getVariantPicker()?.querySelector('.variant-picker__option');
          const error = getVariantPicker()?.querySelector('.variant-picker__error');

          firstOption?.classList.add('ProductForm__Option--error');

          if (error) {
            error.hidden = false;
          }

          firstOption?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          log('Regular ATC blocked: no real Prestige variant selected.');
          restoreBundleDisplay();
          return;
        }

        currentForm.dataset.regularVariantId = regularVariantId;

        log('SUBMITTING REGULAR PRODUCT', {
          regularVariantId,
          formVariantId: currentForm.querySelector('input[name="id"]')?.value,
          kachingSessionDisabled: !!getKachingSessionInput()?.disabled,
          currentDealId
        });

        bypassRegularGuard = true;

        try {
          currentNativeAtc.click();
        } finally {
          bypassRegularGuard = false;
        }

        restoreBundleDisplayAfterCartAdd();

      } catch (error) {
        console.error('[Dual ATC] Could not submit regular product:', error);
        restoreBundleDisplay();
      } finally {
        regularModePromise = null;
      }
    });

    /* BUNDLE ATC */

    productRoot.addEventListener('click', async event => {
      if (!(event.target instanceof Element)) return;

      const clickedBundleAtc = event.target.closest(BUNDLE_ATC_SELECTOR);
      if (!clickedBundleAtc || !getForm()?.contains(clickedBundleAtc)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (bundleSubmitInFlight) {
        log('Bundle submit blocked: already in flight.');
        return;
      }

      bundleSubmitInFlight = true;

      console.group('[Dual ATC] BUNDLE ATC');

      try {
        enableKachingSession();

        log('Bundle ATC clicked', {
          dealId,
          currentDealId
        });

        await ensureBundleActive();
        await nextFrame();

        const currentForm = getForm();
        const currentNativeAtc = getNativeAtc();

        if (!currentForm || !currentNativeAtc) {
          throw new Error('Current Prestige form/ATC not found.');
        }

        currentForm.dataset.kachingBundleSubmit = 'true';

        log('SUBMITTING KACHING BUNDLE', {
          currentDealId,
          formVariantId: currentForm.querySelector('input[name="id"]')?.value,
          session: getKachingSessionInput()?.value,
          sessionDisabled: !!getKachingSessionInput()?.disabled,
          variantIdQuantities: latestVariantState?.variantIdQuantities,
          bundleProducts: latestVariantState?.bundleProducts,
          validation: latestVariantState?.validation
        });

        bypassRegularGuard = true;

        try {
          currentNativeAtc.click();
        } finally {
          bypassRegularGuard = false;

          setTimeout(() => {
            delete currentForm.dataset.kachingBundleSubmit;
            bundleSubmitInFlight = false;
          }, 0);
        }

      } catch (error) {
        bundleSubmitInFlight = false;

        const currentForm = getForm();

        if (currentForm) {
          delete currentForm.dataset.kachingBundleSubmit;
        }

        console.error('[Dual ATC] Could not submit Kaching bundle:', error);
      } finally {
        console.groupEnd();
      }
    }, true);

    /* INITIALIZE */

    async function initializeRegularMode() {
      try {
        if (currentDealId !== null) {
          log('Bundle preselected. Turning it OFF.');
          await ensureBundleInactive();
        }

        disableKachingSession();

        regularVariantId = getPreselectedVariantId();
        regularOptions = [];

        if (!regularVariantId) {
          getNativeOptionInputs().forEach(input => {
            input.checked = false;
          });
        }

        log('INITIALIZED IN REGULAR MODE', {
          dealId,
          currentDealId,
          preselectedFromUrl: regularVariantId,
          kachingSessionDisabled: !!getKachingSessionInput()?.disabled
        });

      } catch (error) {
        console.error('[Dual ATC] Could not initialize:', error);
      } finally {
        const button = getBundleAtc();

        if (button) {
          button.hidden = false;
        }

        // Keeps the bundle box open (with its size dropdowns) by re-selecting it in Kaching.
        // The main ATC handler above switches it off again before adding a single item.
        const { bar } = getKachingElements();

        if (bar) {
          bar.classList.add('kaching-bundles__bar--selected');
          document.querySelector('.kaching-bundles__bar .kaching-bundles__bar-main')?.click();
        }
      }
    }

    initializeRegularMode();
  }

  function init() {
    document.querySelectorAll(PRODUCT_ROOT_SELECTOR).forEach(initDualAtc);
  }

  init();

  const observer = new MutationObserver(init);

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', init);
})();
